import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { css } from '@emotion/react';
import { ArrowClockwise, ArrowSquareOut, Copy, HardDrive, HardDrives, Info, Wrench } from '@phosphor-icons/react';
import { AccessRights } from 'common/account/AccessRights';
import { DeploymentState } from 'common/CommonConstants';
import { trpc, useMutation } from 'client/lib/trpc';
import { useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import type { OnLog } from 'client/state/LogState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { HelpCard } from 'client/pages/deployment/components/HelpCard';
import { Divider } from 'client/components/Divider';
import { RamGraph } from 'client/components/Graph/RamGraph';
import { StatisticGraph } from 'client/components/Graph/StatisticGraph';
import { Content, Sidebar } from 'client/components/Layout/Dashboard';
import { type MenuItemType, MenuList } from 'client/components/Menu';
import { Spectrum } from 'client/components/Spectrum';
import { Stack } from 'client/components/Stack';
import { StackPreview } from 'client/components/StackPreview';
import { Typography } from 'client/components/Typography';
import { CloneDialog } from './components/CloneDialog';
import { ClusterCard } from './components/ClusterCard';
import { DevelopCard } from './components/Develop/DevelopCard';
import { OverviewStatus } from './components/OverviewStatus';
import { PendingChanges } from './components/PendingChanges/PendingChanges';
import { TrialOnboarding } from './components/TrialOnboarding';

interface DeploymentOverviewTabProps {
  onLog: OnLog;
}

export const DeploymentOverviewTab = ({ onLog }: DeploymentOverviewTabProps) => {
  const navigate = useNavigate();
  const { hasAccessRight } = useUser();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const deploymentUrl = useDeploymentClientState((state) => state.document?.url || '');
  const stackConfig = useDeploymentClientState((state) => state.document?.stackConfig);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const deploymentStateID = statusTree?.deploymentStateID || DeploymentState.STOPPED;

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);

  const updateCoreMutation = useMutation(trpc.deployment.updateCore, {
    onSuccess: async (data) => {
      if (data.status == 'ok') {
        console.log('YES!');
      } else {
        console.log('updateCore error', data);
        throw new Error(data.status);
      }
    },
  });

  const handleUpdateCore = () => {
    updateCoreMutation.mutate({ deploymentID: deploymentID || 0 });
  };

  const cloneDeployment = async () => {
    navigate(`clone`);
  };

  const handleCloneDialogOpen = useCallback(() => setCloneDialogOpen(true), []);
  const handleCloneDialogClose = useCallback(() => setCloneDialogOpen(false), []);

  const quickMenuItems: MenuItemType[] = [
    {
      id: 'clone',
      title: 'Clone',
      subtitle: 'Clone this deployment into a new one',
      icon: <Copy />,
      onSelect: handleCloneDialogOpen,
    },
    // {
    //   id: 'logs',
    //   title: 'Logs',
    //   subtitle: 'View various logs from deployment',
    //   icon: <AppWindow />,
    //   links: logListItems,
    //   onSelect: handleLogClick,
    //   disabled: deploymentStateID !== DeploymentState.RUNNING,
    // },
  ];

  // Render admin items in the right sidebar
  if (hasAccessRight(AccessRights.ADMIN_PAGES)) {
    quickMenuItems.push({
      id: 'admin',
      title: 'Admin Only',
      subtitle: 'Diploi professionals only',
      icon: <Wrench />,
      links: [
        {
          id: 'admin-page',
          title: 'Deployment admin page',
          icon: <Info weight="bold" />,
          endIcon: <ArrowSquareOut />,
          href: `/admin/deployments/${deploymentID}`,
        },
        {
          id: 'core-log',
          title: 'Core Log',
          icon: <Wrench weight="bold" />,
          endIcon: <ArrowSquareOut />,
          href: `${deploymentUrl}/logs/admin/core`,
        },
        {
          id: 'core-config-log',
          title: 'Core Config Log',
          icon: <Wrench weight="bold" />,
          endIcon: <ArrowSquareOut />,
          href: `${deploymentUrl}/logs/admin/core-config`,
        },

        {
          id: 'system-log',
          title: 'System Log',
          icon: <Wrench weight="bold" />,
          endIcon: <ArrowSquareOut />,
          href: `${deploymentUrl}/logs/admin/system`,
        },
        {
          id: 'helm-log',
          title: 'Helm Log',
          icon: <Wrench weight="bold" />,
          endIcon: <ArrowSquareOut />,
          href: `${deploymentUrl}/logs/admin/helm`,
        },
        {
          id: 'update-core',
          title: `Update all cores and check components ${updateCoreMutation.isLoading ? 'Loading' : ''} ${
            updateCoreMutation.error ? updateCoreMutation.error.message : ''
          }`,
          icon: <ArrowClockwise weight="bold" />,
          onClick: handleUpdateCore,
        },
      ],
      //onSelect: handleUpdateCore,
      disabled: false,
    });
  }

  return (
    <>
      <Content>
        <TrialOnboarding />

        <PendingChanges />

        <DevelopCard />

        {/* 
        <Spectrum deploymentID={deploymentID} label="Status" slices={71} />
        <Divider plain />
        */}

        <OverviewStatus onLog={onLog} />
      </Content>

      <Sidebar css={sidebarStyle}>
        <ClusterCard />
        {/* <DevelopCard /> */}
        {/* <QuickLaunchCard /> */}
        {/* <StackPreview title="Deployment Stack" description="Preview of the current active stack." stackConfig={stackConfig} /> */}
        <HelpCard />
        <MenuList items={quickMenuItems} css={menuStyle} />
      </Sidebar>

      <CloneDialog
        open={cloneDialogOpen}
        disabled={deploymentStateID !== DeploymentState.RUNNING}
        onClone={cloneDeployment}
        onClose={handleCloneDialogClose}
      />
    </>
  );
};

const connectionsStyle = css`
  margin-bottom: 32px;

  .accordion-content {
    &[data-state='open'] {
      margin-bottom: 24px;
    }
    &[data-state='closed'] {
      margin-bottom: 0;
    }
  }
`;

const menuStyle = css`
  padding: 0;

  > li:first-of-type .MuiListItemSecondaryAction-root {
    display: none;
  }
`;

const sidebarStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
